import { MarkerClusterer } from "@googlemaps/markerclusterer"
import { Wrapper } from "@googlemaps/react-wrapper"
import GoogleMapReact from "google-map-react"
import moment from "moment"
import { useContext, useEffect, useState } from "react"
import DeviceMarker, { openDeviceInfoWindow } from "./DeviceMarker"
import { MapContext } from "./MapContextProvider"
import MarkerIcon from "./MarkerIcon"
import MarkerIconArrow from "./MarkerIconArrow"
import { IDeviceHistoryPosition, IDevicePosition } from "./devices.interface"
import { mapStyles } from "./mapStyles"
import { useTranslation } from "react-i18next"

const FullscreenMap = () => {
  const { t } = useTranslation()
  const { mapRef, mapType, devices, historyPositions, onMarkerClick, activeMarker, setLoadingMarkers } =
    useContext(MapContext)
  const [currentZoom, setZoom] = useState<any>(7)

  const [googleHistoryMarkers, sethistoryMarkers] = useState<any>([])
  const [googleLiveMarkers, setLiveMarkers] = useState<any>([])
  const [googlePolylines, setPolylines] = useState<google.maps.Polyline | undefined>()
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | undefined>()
  const [cluster, setCluster] = useState<MarkerClusterer | undefined>()
  const FALLBACK_CENTER = { lat: 57.425643, lng: 18.587717 }
  const [center, setCenter] = useState(FALLBACK_CENTER)

  useEffect(() => {
    if (activeMarker === null && googleHistoryMarkers.length > 0) {
      clearHistoryMarkers()
      infoWindow?.close()
    }
  }, [activeMarker])

  useEffect(() => {
    if (!activeMarker?.currentPosition || !infoWindow) return

    const map = mapRef.current
    const marker = googleLiveMarkers?.find((item: any) => item.get("deviceId") === activeMarker.id)
      || googleLiveMarkers?.find((item: any) => item.title === activeMarker.label)
    if (!map || !marker) return

    openDeviceInfoWindow(infoWindow, map, marker, activeMarker)
  }, [activeMarker, infoWindow, googleLiveMarkers])

  useEffect(() => {
    var map = mapRef.current

    if (map) {
      devices
        .filter((d: IDevicePosition) => d.currentPosition !== null)
        .map((device: IDevicePosition) => {
          let existingMarker: google.maps.Marker = googleLiveMarkers?.find(
            (x: any) => x.get("deviceId") === device.id || device.label === x.title
          )

          existingMarker?.set("deviceId", device.id)
          existingMarker?.setTitle(device.label)

          if (existingMarker === undefined) {
            // Add new marker if not exist
            const marker = DeviceMarker(device, map, onMarkerClick, infoWindow, activeMarker)

            setLiveMarkers((prevState: any) => [...prevState, marker])

            return marker
          } else if (
            device.currentPosition.lat !== existingMarker.getPosition()?.lat() &&
            device.currentPosition.long !== existingMarker.getPosition()?.lng()
          ) {
            // Update marker if position changed
            const rotation =
              google.maps &&
              google.maps.geometry.spherical.computeDistanceBetween(
                existingMarker.getPosition() as google.maps.LatLng,
                {
                  lat: device.currentPosition.lat,
                  lng: device.currentPosition.long
                }
              )

            existingMarker.addListener("click", () => {
              infoWindow?.setContent(
                `${moment(device?.currentPosition?.timestamp).format("YYYY-MM-DD HH:mm")} <br> ${t("map.speed", "Speed")}: ${device?.speed
                } ${t("map.km_h", "km/h")}`
              )
              infoWindow?.open(map, existingMarker)
              var latLng = existingMarker.getPosition()
              map.setCenter(latLng)
            })
            existingMarker.setPosition({ lat: device.currentPosition.lat, lng: device.currentPosition.long })
            MarkerIcon(existingMarker, rotation, device, activeMarker)
          }
          return existingMarker
        })

      cluster && cluster.addMarkers(googleLiveMarkers)
    }
  }, [devices, mapRef, onMarkerClick])

  useEffect(() => {
    clearHistoryMarkers()

    var map = mapRef.current
    if (map) {
      let markers: any = []
      historyPositions.map((device: IDeviceHistoryPosition) => {
        var marker = new google.maps.Marker({
          position: { lat: device.latitude!, lng: device.longitude! },
          map,
          visible: false
        })

        MarkerIconArrow(marker, device.angle)

        marker.addListener("click", () => {
          infoWindow?.setContent(
            `${moment(device.timestamp).format("YYYY-MM-DD HH:mm")} <br> ${t("map.speed", "Speed")}: ${device.speed} ${t("map.km_h", "km/h")}`
          )
          infoWindow?.open(map, marker)
        })

        sethistoryMarkers((prevState: IDeviceHistoryPosition[]) => [...prevState, marker])
        markers.push(new google.maps.LatLng(device.latitude!, device.longitude))

        return marker
      })

      googlePolylines?.setPath(markers)
      map && googlePolylines?.setMap(map)
      var lastHistoryMarker = markers?.slice(-1)[0]

      lastHistoryMarker &&
        map.setCenter(new google.maps.LatLng(lastHistoryMarker.lat(), lastHistoryMarker.lng()))
      setLoadingMarkers(false)
    }
  }, [historyPositions, mapRef])

  const clearHistoryMarkers = () => {
    googlePolylines?.setMap(null)
    for (let i = 0; i < googleHistoryMarkers.length; i++) {
      googleHistoryMarkers[i].setMap(null)
    }
    sethistoryMarkers([])
  }

  const hideAllMarkers = () => {
    for (let i = 0; i < googleHistoryMarkers.length; i++) {
      googleHistoryMarkers[i].setVisible(false)
    }
  }

  useEffect(() => {
    hideAllMarkers()
    if (googleHistoryMarkers.length > 0)
      if (currentZoom >= 17) {
        for (let i = 0; i < googleHistoryMarkers.length; i++) {
          googleHistoryMarkers[i].setVisible(true)
        }
        return
      } else if (currentZoom <= 17 && currentZoom >= 15) {
        for (let i = 0; i < googleHistoryMarkers.length; i += 20) {
          googleHistoryMarkers[i].setVisible(true)
        }
        return
      } else if (currentZoom <= 15 && currentZoom >= 10) {
        for (let i = 0; i < googleHistoryMarkers.length; i += 50) {
          googleHistoryMarkers[i].setVisible(true)
        }
        return
      } else if (currentZoom <= 10) {
        for (let i = 0; i < googleHistoryMarkers.length; i += 400) {
          googleHistoryMarkers[i].setVisible(true)
        }
        return
      }
  }, [currentZoom, googleHistoryMarkers])

  const zoomChanged = () => {
    var zoom = mapRef.current.getZoom()
    setZoom(zoom)
  }
  const handleApiLoaded = (map: google.maps.Map) => {
    mapRef.current = map
    setCluster(new MarkerClusterer({ map }))
    var polyline = new google.maps.Polyline({
      geodesic: true,
      strokeColor: "#83d4e7",
      strokeOpacity: 1,
      strokeWeight: 3
    })

    const infoWindow = new google.maps.InfoWindow({
      content: "",
      disableAutoPan: true
    })
    infoWindow.addListener("closeclick", () => {
      console.log("close")
    })
    setInfoWindow(infoWindow)
    setPolylines(polyline)
  }

  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser.")
      setCenter(FALLBACK_CENTER)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            console.warn("Location access blocked. Please enable it in your browser settings.")
            break
          case error.POSITION_UNAVAILABLE:
            console.warn("Location information is unavailable.")
            break
          case error.TIMEOUT:
            console.warn("Location request timed out.")
            break
          default:
            console.error("Unknown geolocation error:", error)
        }
        setCenter(FALLBACK_CENTER)
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 60_000
      }
    )
  }, [])


  return (
    <Wrapper apiKey={"AIzaSyCk4SCnuwj2yKZ_Z8CqIasGQIIiMh0lws8"}>
      <GoogleMapReact
        style={{
          marginTop: "-53px",
          height: "100vh",
          width: "100%",
          pointerEvents: "all",
          zIndex: "5"
        }}
        bootstrapURLKeys={{
          key: "AIzaSyCk4SCnuwj2yKZ_Z8CqIasGQIIiMh0lws8"
        }}
        center={center}
        defaultZoom={currentZoom}
        options={{
          mapTypeControl: false,
          zoomControl: true,
          styles: mapStyles,
          disableDefaultUI: true,
          mapTypeId: `${mapType}`,
          mapId: "38e89e5c91f932d2",
          clickableIcons: false
        }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
          handleApiLoaded(map)
        }}
        onZoomAnimationEnd={() => zoomChanged()}></GoogleMapReact>
    </Wrapper>
  )
}
export default FullscreenMap
